import React, {useState, useEffect, useRef} from 'react';
import { withStyles, Typography, Button, Link, Dialog, DialogTitle, DialogContent, CircularProgress } from '@material-ui/core';
import { connect } from 'react-redux';
import UserActivityList from './UserActivityList';
import { hasPermission, formatDate } from '../../sc-utilities';
import { isSuperUser } from '../../sc-utilities';
import moment from 'moment';
import { RemoveCircleOutline, More, Update } from '@material-ui/icons';
import * as actions from './store/userActivityActions';
import { snackActions } from '../../sc-utilities/SnackBar';
import { informationColors } from '../../../themes';
import { CancelToken } from 'axios';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='statusActive']": {
            color: theme.palette.success.dark
        },
        "[class*='statusInactive']": {
            color: theme.palette.error.dark
        },
        "[class*='button']": {
            textTransform: 'none',
            textDecoration: 'none',
            minWidth: '24px',
            borderRadius: '1px'
        }
    },
    button: {
        minWidth: '24px',
        borderRadius: '1px',
        textTransform: 'none',
        textDecoration: 'none'
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    }
});

const UserActivity = props => {
    const tableRef = useRef();
    const { classes, translations, permissions, onFetchClientTypes, columnsVisible, activeCustomerID } = props;
    const [userAgentDialog, setUserAgentDialog] = useState({ open: false, sessionDialog: false });
    const [extendSessionModal, setExtendSessionModal] = useState({ open: false, rowData: null });
    let visible = columnsVisible && columnsVisible['UserActivity'];
    const listSource = CancelToken.source();

    const [clientTypes, setClientTypes] = useState({});
    const [columns, setColumns] = useState(null);

    let tableColumns = [
        {
            title: translations["username"] || 'Username', field: 'Username',
            hidden: visible ? visible['Username'] : false,
            render: rowData => {
                return <Button className={classes.button} color="primary" variant="outlined" component={Link} href={`/users/details?userID=${rowData.UserID}`}><Typography variant={'caption'}>{rowData.User.Username ? rowData.User.Username : translations['short_not_assigned'] || 'n/a'}</Typography></Button>;
            }
        },
        {
            title: translations["login_credential"] || 'Login credential', field: 'LoginCredential',
            hidden: visible ? visible['LoginCredential'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.LoginCredential ? rowData.LoginCredential : translations['short_not_assigned'] || 'n/a'}</Typography>;
            },
        },
        {
            title: translations["created_at"] || 'Created at', field: 'CreatedAt',
            hidden: visible ? visible['CreatedAt'] : false,
            type: 'datetime',
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.CreatedAt ? formatDate(rowData.CreatedAt) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["last_activity"] || 'Last activity', field: 'LastActivity',
            hidden: visible ? visible['LastActivity'] : false,
            type: 'datetime',
            defaultFilter: moment().subtract(1, 'days'),
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.LastActivity ? formatDate(rowData.LastActivity) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["status"] || 'Status', field: 'Status',
            hidden: visible ? visible['Status'] : false,
            lookup: { 'active': translations['active'] || 'Active', 'inactive': translations['inactive'] || 'Inactive' },
            defaultFilter: ['active'],
            render: rowData => {
                return <>
                    <CustomChip
                        className={rowData.Status === 'active' ? 'active' : 'inactive'}
                        key={'customer_status'}
                        value={rowData.Status === 'active' ? translations['active'] || 'Active' : translations['inactive'] || 'Inactive'}
                    />
                </>;
            }
        },
        {
            title: translations["client_type"] || 'Client type', field: 'ClientType',
            hidden: visible ? visible['ClientType'] : false,
            lookup: clientTypes,
            defaultFilter: Object.keys(clientTypes),
            render: rowData => {
                return <Typography variant={'caption'}> {rowData.ClientType ? clientTypes[rowData.ClientType] : translations['short_not_assigned'] || 'n/a'}</Typography>
            }
        },
        {
            title: translations["client_version"] || 'Client version', field: 'ClientVersion',
            hidden: visible ? visible['ClientVersion'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ClientVersion ? rowData.ClientVersion : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["logged_with"] || 'Logged with', field: 'LoggedWith',
            hidden: visible ? visible['LoggedWith'] : false,
            lookup: { 'username': translations['username'] || 'Username', 'email': translations['email'] || 'Email' },
            defaultFilter: ['username', 'email'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.LoggedWith ? translations[rowData.LoggedWith] || rowData.LoggedWith : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        }
    ];


    useEffect(() => {
        const fetchClientTypes = async () => {
            let clientTypeData = await onFetchClientTypes({ ActiveCustomerID: activeCustomerID }, { cancelToken: listSource });
            let clientTranslations = {};
            for (const key in clientTypeData) {
                if (clientTypeData.hasOwnProperty(key)) {
                    clientTranslations[key] = translations[`${key}`] || clientTypeData[key];
                }
            }
            setClientTypes(clientTranslations);
        }
        fetchClientTypes();
        return () => {
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (Object.keys(clientTypes).length) {
            setColumns(tableColumns);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientTypes]);

    const handleRemoveSession = async (event, removeData) => {
        if (!event) {
            setUserAgentDialog({ open: false });
            return;
        }
        setUserAgentDialog({ ...userAgentDialog, sessionDialog: true, SessionID: removeData.SessionID, user: removeData && removeData.User && removeData.User.Username ? removeData.User.Username : '' });
    };

    const handleExtendSession = async (rowData) => {
        setExtendSessionModal({open: true, rowData: rowData});
    };

    const tableActions = [
        rowData => ({
            icon: Update,
            tooltip: rowData.Status === 'inactive' ? null : translations['extend_session'] || 'Extend session',
            onClick: (event, rowData) => handleExtendSession(rowData),
            disabled: rowData.Status === 'inactive',
            hidden: !hasPermission(permissions, "update_user_session") && !isSuperUser()
        }),
        rowData => ({
            icon: RemoveCircleOutline,
            tooltip: rowData.Status === 'inactive' ? null : translations['remove_session'] || 'Remove session',
            onClick: (event, rowData) => handleRemoveSession(event, rowData),
            disabled: rowData.Status === 'inactive',
            hidden: !isSuperUser()
        }),
        rowData => ({
            icon: More,
            hidden: !isSuperUser(),
            tooltip: translations['user_agent_details'] || 'User agent details',
            onClick: (event, rowData) => {
                try {
                    let userAgentInfo = JSON.parse(rowData.UserAgentInformation);
                    if (!userAgentInfo) {
                        throw new Error('No user agent information');
                    }
                    setUserAgentDialog({ ...userAgentDialog, open: true, details: userAgentInfo });
                } catch (error) {
                    snackActions.snackBarComponent('No user agent information', 'info');
                }
            },
        })
    ];

    if (!isSuperUser()) {
        tableColumns.splice(2, 3);
        tableColumns.shift();
    };

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'user_session_list') ?
                columns ? <UserActivityList clientTypes={clientTypes} extendSessionModal={extendSessionModal} setExtendSessionModal={setExtendSessionModal} handleRemoveSession={handleRemoveSession} userAgentDialog={userAgentDialog} tableColumns={columns} tableActions={tableActions} tableRef={tableRef} /> : <CircularProgress />
                : null}
            {userAgentDialog.open ?
                <Dialog
                    id="user-agent-dialog-title"
                    open={userAgentDialog.open}
                    disableBackdropClick={false}
                    disableEscapeKeyDown={false}
                    disableEnforceFocus={false}
                    fullWidth={true}
                    onClose={() => setUserAgentDialog({ open: false })}
                >
                    <DialogTitle id="user-agent-dialog-title">{translations['user_agent_information'] || 'User agent information'}</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant={'caption'} gutterBottom><b>{translations['browser'] || 'Browser'}: </b> {userAgentDialog.details.browser}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['browser_version'] || 'Browser version'}: </b> {userAgentDialog.details.browserVersion}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['os'] || 'OS'}: </b> {userAgentDialog.details.os}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['platform'] || 'Platform'}: </b> {userAgentDialog.details.platform}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['ip'] || 'IP'}: </b> {userAgentDialog.details.ip}</Typography><br />
                    </DialogContent>
                </Dialog>
                : null}
        </div >
    );
};

const CustomChip = props => {
    const { keyValue, value, className } = props;
    let possibleStyles = {
        inactive: {
            color: informationColors.error.dark,
        },
        active: {
            color: informationColors.success.main
        }
    }
    return (
        <Typography
            variant={'caption'}
            key={keyValue}
            className={className}
            style={possibleStyles[className]}>{value === 'n/a' ? value : capitalize(value)}</Typography>
    );
};

const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
}

const mapStateToProps = state => {
    return {
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        columnsVisible: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        removeSession: queryParams => dispatch(actions.removeSession(queryParams)),
        onFetchClientTypes: (data, option) => dispatch(actions.fetchClientTypes(data, option))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(UserActivity));